import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRotate } from '@fortawesome/free-solid-svg-icons';
import { faSteam as fabFaSteam } from '@fortawesome/free-brands-svg-icons';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';

import store from 'Redux/store';
import { closeSteamModal } from 'Redux/ducks/modal';
import { unlink, getRedirectUrl, refresh } from 'Redux/ducks/steam';
import SteamError from 'Components/Account/LinkedAccounts/SteamError';
import SteamSuccess from 'Components/Account/LinkedAccounts/SteamSuccess';
import SteamSync from 'Components/Account/LinkedAccounts/SteamSync';
import Video from 'Components/Utility/Video';

const SteamConnect = ({ steam, search, isModal }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const urlProps = queryString.parse(search);

    if (urlProps.steamErrorCode) {
      setError({
        code: urlProps.steamErrorCode,
        message: urlProps.steamErrorMessage,
        steamExistingEmail: urlProps.steamExistingEmail,
      });
    }

    if (urlProps.success && urlProps.success === 'true') {
      setSuccess('connect');
    }
  }, [search]);

  const unlinkSteam = () => {
    setIsConnecting(true);
    setSuccess(null);
    setError(null);
    unlink().then(() => {
      setIsConnecting(false);
      setSuccess('disconnect');
    }).catch(setError);
  };

  const connectSteam = () => {
    setIsConnecting(true);
    setSuccess(null);
    setError(null);
    getRedirectUrl().then((url) => { window.location.href = url; }).catch(setError);
  };

  const refreshSteam = () => {
    setIsConnecting(true);
    setSuccess(null);
    setError(null);
    refresh().then((updated) => {
      setIsConnecting(false);
      setSuccess('refresh');
      if (isModal && updated && updated.steam_id && !updated.isLimitedAccount) {
        store.dispatch(closeSteamModal());
      }
    }).catch(setError);
  };

  return (
    <>
      {success ?
        <SteamSuccess type={success} closeFunction={() => setSuccess(null)} />
        :
        <SteamError error={error} closeFunction={() => setError(null)} />
      }
      {steam && steam.steam_id ?
        <>
          <div className="account-connection">
            <div className="connection-icon steam">
              <FontAwesomeIcon icon={fabFaSteam} title="Steam" />
            </div>
            <div className="connection-text-container">
              <div className="main-text">
                <FormattedMessage id="steam-connect.linked" defaultMessage="Linked to " />
                <b>{steam.displayName}</b>
              </div>
              <div className="connection-unlink">
                <button type="button" className="btn btn-unlink-account" onClick={unlinkSteam}>
                  <FormattedMessage id="steam-connect.disconnect" defaultMessage="Disconnect" />
                </button>
              </div>
            </div>
            {steam.avatarFull &&
              <div className="connection-profile-pic">
                <img src={steam.avatarFull} alt={`${steam.displayName} Steam Avatar`} />
              </div>
            }
          </div>

          {steam.privacyState !== 'public' && !isModal &&
            <div className="linked-account-info-block">
              <h4>
                <FormattedMessage id="connections.video.title" defaultMessage="Check your Steam privacy settings to enable wishlist syncing" />
              </h4>
              <p className="steam-profile-private">
                <FormattedMessage id="connections.main.warning.private" defaultMessage="Your Steam profile seems to be set to private. Please follow the guide below to set your profile to public and retry connecting." />
              </p>
              <div className="steam-profile-private-video">
                <div className="video-container">
                  <div className="video-wrapper w-100">
                    <Video width="100%" height="auto" src="0LVcyReCoSo" className="youtube-video" />
                  </div>
                </div>
                <div className="text-container">
                  <p>
                    <FormattedMessage
                      id="connections.video.text"
                      defaultMessage="We can only import public information. Please make sure that your profile has been set to public. Go to Steam, view your profile, click 'Edit', then go to 'My Privacy Settings' and choose the 'Public' option for 'My Profile' and 'Game details'."
                    />
                  </p>
                </div>
              </div>
            </div>
          }

          {!!steam.isLimitedAccount &&
            <React.Fragment>
              <p className="steam-profile-limited">
                <FormattedMessage
                  id="connections.main.warning.limited"
                  defaultMessage="Your steam account appears to be 'limited'. Only 'Unlimited' accounts are eligble for Fanatical giveaways. An unlimited Steam account is one that has spent at least $5.00 USD within the Steam store. You can read more about Limited Steam Accounts {link}."
                  values={{
                    link: <a href="https://help.steampowered.com/en/faqs/view/71D3-35C2-AD96-AA3A" target="_blank" rel="noopener noreferrer" className="brand-link">
                      <FormattedMessage id="steam-connect.here" defaultMessage="here" />
                    </a>,
                  }}
                />
              </p>
            </React.Fragment>
          }

          {(steam.privacyState !== 'public' || !!steam.isLimitedAccount) &&
            <React.Fragment>
              <Button
                disabled={isConnecting}
                color="primary"
                className="connect-btn steam-connect-btn steam-refresh-btn"
                onClick={refreshSteam}
              >
                <div className="steam-btn-icon">
                  {isConnecting ?
                    <FontAwesomeIcon spin icon={faSpinner} />
                    :
                    <FontAwesomeIcon icon={faRotate} />
                  }
                </div>
                <b>
                  {isConnecting ?
                    <FormattedMessage id="steam-connect.refreshing" defaultMessage="Refreshing" />
                    :
                    <FormattedMessage id="steam-connect.refresh-account" defaultMessage="Refresh your Steam account connection" />
                  }
                </b>
              </Button>

              <p>
                <FormattedMessage id="steam-connect.update-time-message" defaultMessage="Updating your Steam profile can take a few minutes for the changes to be visible to Fanatical." />
              </p>
            </React.Fragment>
          }

          {!isModal && steam.privacyState === 'public' &&
            <SteamSync
              steamId={steam.steam_id}
              syncSettings={steam.syncSettings}
              setError={setError}
              setSuccess={setSuccess}
            />
          }
        </>
        :
        <>
          {!isModal &&
            <div className="account-connection">
              <div className="connection-icon steam">
                <FontAwesomeIcon icon={fabFaSteam} title="Steam" />
              </div>
              <div className="connection-text-container">
                <div className="main-text">
                  <FormattedMessage id="steam-connect.account.not-linked" defaultMessage="Steam Account Not Linked" />
                </div>
                <div className="connection-unlink">
                  <button
                    disabled={isConnecting}
                    className="btn btn-link btn-link-account"
                    onClick={connectSteam}
                  >
                    <FormattedMessage id="steam-connect.account.link-account" defaultMessage="Link Steam Account" />
                  </button>
                </div>
              </div>
            </div>
          }
          {isModal &&
            <div className="account-connection account-connection-modal">
              <Button
                disabled={isConnecting}
                className="connect-btn steam-connect-btn"
                onClick={connectSteam}
              >
                <div className="steam-btn-icon">
                  {isConnecting ?
                    <FontAwesomeIcon spin icon={faSpinner} />
                    :
                    <FontAwesomeIcon icon={fabFaSteam} title="Steam" />
                  }
                </div>
                <b>
                  {isConnecting ?
                    <FormattedMessage id="steam-connect.connecting" defaultMessage="Connecting" />
                    :
                    <FormattedMessage id="steam-connect.link-account" defaultMessage="Link your Steam account" />
                  }
                </b>
              </Button>
            </div>
          }
        </>
      }
    </>
  );
};

SteamConnect.defaultProps = {
  isModal: false,
};

SteamConnect.propTypes = {
  isModal: PropTypes.bool,
};

const mapStateToProps = ({
  auth: { steam },
  router: { location: { search } },
}) => ({
  steam,
  search,
});

export default connect(mapStateToProps)(SteamConnect);
